import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { colors, fontWeight } from "../design-system";
import { motion } from "../design-system/motion";
import { AppText } from "./AppText";

// Système de toast léger pour les feedbacks NON BLOQUANTS (succès de mutation,
// erreur réseau, info). 4 types : info, success, warning, danger. Disparition
// automatique après 3s par défaut.

export type ToastType = "info" | "success" | "warning" | "danger";

type ShowOptions = {
  type?: ToastType;
  message: string;
  duration?: number; // ms — défaut 3000
};

type ToastContextValue = {
  show: (opts: ShowOptions) => void;
  hide: () => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

// Fond plein = cran 600 calibré pour du texte blanc (règle Base) : le blanc sur
// success500 faisait 2.5:1 et sur or500 2.9:1 — illisible. Et le warning porte
// la famille warn* : un toast d'avertissement ne doit pas avoir la couleur du
// CTA « Confirmer » (marque).
const TYPE_COLORS: Record<ToastType, { bg: string; fg: string; emoji: string }> = {
  info: { bg: colors.navy800, fg: colors.white, emoji: "ℹ" },
  success: { bg: colors.success600, fg: colors.white, emoji: "✓" },
  warning: { bg: colors.warn600, fg: colors.white, emoji: "⚠" },
  danger: { bg: colors.sos600, fg: colors.white, emoji: "✕" },
};

const DEFAULT_TTL_MS = 3_000;

/**
 * ## Portage : Reanimated → transition CSS
 * La version native pilotait `opacity` et `translateY` par `useSharedValue` +
 * `withTiming`, avec un `runOnJS(finalize)` en fin de sortie pour démonter le
 * toast. Ici, un simple booléen `entered` bascule les propriétés CSS et la
 * transition fait le reste ; le démontage suit un `setTimeout` calé sur la
 * durée de sortie.
 *
 * Le respect du réglage système « réduire les animations » n'a plus besoin
 * d'être déclaré par animation (`reduceMotion: motion.reduce`) : la règle
 * globale `prefers-reduced-motion` d'index.css ramène les transitions à 1ms —
 * assez court pour être imperceptible, assez long pour que le timer de
 * démontage reste cohérent.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ShowOptions | null>(null);
  const [entered, setEntered] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (unmountTimer.current) clearTimeout(unmountTimer.current);
    hideTimer.current = null;
    unmountTimer.current = null;
  }, []);

  const hide = useCallback(() => {
    clearTimers();
    setEntered(false);
    // Démontage APRÈS la transition de sortie, sinon l'élément disparaît d'un
    // coup au lieu de s'effacer.
    unmountTimer.current = setTimeout(() => setCurrent(null), motion.duration.fast);
  }, [clearTimers]);

  const show = useCallback(
    (opts: ShowOptions) => {
      clearTimers();
      setCurrent(opts);
      setEntered(false);
      // Un frame d'écart avant de passer à l'état « entré » : sans ça, le
      // navigateur peint directement l'état final et il n'y a aucune
      // transition à interpoler.
      requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));

      const ttl = opts.duration ?? DEFAULT_TTL_MS;
      hideTimer.current = setTimeout(hide, ttl);
    },
    [clearTimers, hide],
  );

  useEffect(() => clearTimers, [clearTimers]);

  // Expose l'API aux appelants hors React (services, stores).
  useEffect(() => {
    bindImperativeToast({ show, hide });
  }, [show, hide]);

  const palette = current ? TYPE_COLORS[current.type ?? "info"] : null;

  // ⚠️ MÉMORISÉ, et ce n'est pas un raffinement.
  //
  // `value={{ show, hide }}` construisait un objet NEUF à chaque rendu du
  // provider — et celui-ci se rend à chaque toast, puisqu'il porte l'état du
  // message affiché. `useToast()` rendait donc une identité différente à chaque
  // fois, alors même que `show` et `hide` sont stables.
  //
  // Pour tout consommateur qui met `toast` dans les dépendances d'un effet —
  // ce que la règle `react-hooks/exhaustive-deps` EXIGE — cela produit une
  // boucle infinie : l'effet affiche un toast, le provider se rend, l'identité
  // change, l'effet se rejoue. Le premier consommateur réel de ce hook
  // (`useAgentPayoutNotifier`) l'a déclenchée ; le défaut dormait ici depuis
  // l'écriture du composant, faute d'appelant.
  const value = useMemo(() => ({ show, hide }), [show, hide]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* La région vit en permanence dans le DOM, même vide : un lecteur
          d'écran n'annonce le contenu d'une live region que si elle existait
          AVANT la mise à jour. La monter en même temps que le message — ce que
          faisait la version native — revient à ne rien annoncer du tout. */}
      <div
        role="status"
        aria-live="polite"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          zIndex: 10_000,
          // Le conteneur couvre toute la largeur : sans ça, il intercepterait
          // les clics sur l'en-tête de l'app même quand aucun toast n'est là.
          pointerEvents: "none",
        }}
      >
        {current && palette && (
          <button
            type="button"
            onClick={hide}
            aria-label="Masquer la notification"
            style={{
              width: "100%",
              maxWidth: 520,
              borderRadius: 14,
              padding: "12px 14px",
              border: "none",
              cursor: "pointer",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              textAlign: "left",
              backgroundColor: palette.bg,
              boxShadow: "0 4px 10px rgba(0,0,0,0.22)",
              pointerEvents: "auto",
              opacity: entered ? 1 : 0,
              transform: entered ? "translateY(0)" : "translateY(-20px)",
              transition: `opacity ${motion.durationCss.base} ${motion.easing.enter}, transform ${motion.durationCss.base} ${motion.easing.enter}`,
            }}
          >
            <span
              aria-hidden
              style={{ fontSize: 16, fontWeight: fontWeight.extraBold, color: palette.fg }}
            >
              {palette.emoji}
            </span>
            <AppText
              variant="labelBold"
              color={palette.fg}
              style={{
                flex: 1,
                // Remplace `numberOfLines={3}` : coupe proprement au-delà de
                // trois lignes plutôt que de laisser filer un message long.
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {current.message}
            </AppText>
          </button>
        )}
      </div>
    </ToastContext.Provider>
  );
}

// Hook réactif — doit être appelé sous un ToastProvider.
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast() doit être appelé sous un <ToastProvider>.");
  }
  return ctx;
}

// API impérative pour les contextes hors React (services, stores). Posée par
// ToastProvider au montage.
let imperativeRef: ToastContextValue | null = null;

export function bindImperativeToast(ctx: ToastContextValue) {
  imperativeRef = ctx;
}

export const toast = {
  show: (opts: ShowOptions) => imperativeRef?.show(opts),
  success: (message: string) => imperativeRef?.show({ type: "success", message }),
  error: (message: string) => imperativeRef?.show({ type: "danger", message }),
  warn: (message: string) => imperativeRef?.show({ type: "warning", message }),
  info: (message: string) => imperativeRef?.show({ type: "info", message }),
};
